import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  Award, 
  Calendar,
  Hash,
  User,
  Shield,
  Home,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CertificateVerificationPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyCertificate();
  }, [certificateId]);

  const verifyCertificate = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/certificates/verify/${certificateId}`
      );

      if (response.ok) {
        const data = await response.json();
        setVerification(data);
      } else {
        setVerification({ valid: false, message: 'Certificate not found' });
      }
    } catch (error) {
      console.error('Error verifying certificate:', error);
      setVerification({ 
        valid: false, 
        message: 'Failed to verify certificate. Please try again later.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const issuedDate = verification?.valid && verification.issued_at
    ? new Date(verification.issued_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'N/A';

  return (
    <div className="min-h-screen bg-slate-50 py-16 px-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">Certificate Verification</h1>
          </div>
          <p className="text-lg text-slate-600">
            Verify the authenticity of RecruiterIQ certificates
          </p>
        </div>

        {/* Verification Result Card */}
        {verification?.valid ? (
          <Card className="border-2 border-green-500 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-200">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-green-900">
                    ✓ Valid Certificate
                  </CardTitle>
                  <p className="text-green-700 mt-1">
                    This certificate has been verified and is authentic
                  </p>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-8">
              <div className="space-y-6">
                {/* Certificate Details */}
                <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                        Recipient Name
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {verification.user_name}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                          Certification Level
                        </p>
                        <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-base">
                          {verification.certification_level.toUpperCase()} TA Professional
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                          Date Issued
                        </p>
                        <p className="text-lg font-semibold text-slate-900">
                          {issuedDate}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Hash className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                          Certificate ID
                        </p>
                        <p className="text-sm font-mono text-slate-700">
                          {verification.certificate_id}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                          Badge Number
                        </p>
                        <p className="text-sm font-mono font-semibold text-green-600 break-all">
                          {verification.badge_number}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Issuer Information */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                        Issued By
                      </p>
                      <p className="text-xl font-bold text-blue-600">
                        {verification.issuer || 'RecruiterIQ'}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Professional Talent Acquisition Certification Program
                      </p>
                    </div>
                    <Award className="w-16 h-16 text-blue-200" />
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-900 mb-2">Verification Guarantee</h3>
                      <p className="text-sm text-blue-700 leading-relaxed">
                        This certificate has been verified against RecruiterIQ's certification database 
                        and is confirmed to be authentic. All certificate details including recipient 
                        name, date, and credentials have been validated.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-red-500 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50 border-b-2 border-red-200">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-full">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-red-900">
                    Invalid Certificate
                  </CardTitle>
                  <p className="text-red-700 mt-1">
                    This certificate could not be verified
                  </p>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-8">
              <div className="space-y-4">
                <p className="text-slate-700">
                  {verification?.message || 'The certificate you are trying to verify could not be found in our database.'}
                </p>
                
                <div className="bg-slate-50 border-l-4 border-l-yellow-500 p-4 rounded">
                  <p className="text-sm text-slate-700">
                    <strong>Note:</strong> If you believe this is an error, please contact the certificate holder 
                    or reach out to RecruiterIQ support at{' '}
                    <a href="mailto:support@therecruiteriq.com" className="text-blue-600 hover:underline">
                      support@therecruiteriq.com
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>About RecruiterIQ Certifications</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 mb-4">
              RecruiterIQ offers professional certifications in Talent Acquisition, designed to validate 
              expertise in recruiting, sourcing strategies, interviewing techniques, and recruitment best practices.
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Industry-recognized certification program</li>
              <li>Comprehensive assessment of TA knowledge and skills</li>
              <li>Peer-reviewed recommendation system</li>
              <li>Lifetime certificate validity</li>
            </ul>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-center">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to RecruiterIQ Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerificationPage;
